"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { publicStorePath } from "@/lib/storefront/public-url";

type Status = "idle" | "loading" | "success" | "error";
const HOME_CONTACT_TOPICS = ["kit", "order", "documentation", "other"] as const;
type HomeContactTopic = (typeof HOME_CONTACT_TOPICS)[number];
type ApiContactTopic = "orders" | "returns" | "partnership" | "other";

export function mapHomeContactTopicToApi(topic: HomeContactTopic): ApiContactTopic {
	return topic === "order" ? "orders" : "other";
}

interface HomeContactFormProps {
	locale: string;
}

export function HomeContactForm({ locale }: HomeContactFormProps) {
	const t = useTranslations("contact");
	const kitT = useTranslations("kitWebsite");
	const topics = [
		{ id: "kit", label: kitT("contact.topicKit") },
		{ id: "order", label: kitT("contact.topicOrder") },
		{ id: "documentation", label: kitT("contact.topicDocumentation") },
		{ id: "other", label: kitT("contact.topicOther") },
	];
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [topic, setTopic] = useState<HomeContactTopic | "">("");
	const [message, setMessage] = useState("");
	const [consent, setConsent] = useState(false);
	const [status, setStatus] = useState<Status>("idle");
	const [errorMsg, setErrorMsg] = useState("");

	const isValid =
		name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && topic && message.trim() && consent;

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!isValid || status === "loading") return;
		setStatus("loading");
		setErrorMsg("");

		const apiTopic = mapHomeContactTopicToApi(topic);
		try {
			const res = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name: name.trim(),
					email: email.trim(),
					topic: apiTopic,
					message: message.trim(),
					consent: true,
					locale,
					contactSource: "homepage-mobile",
				}),
			});
			if (!res.ok) {
				const data = (await res.json().catch(() => ({}))) as { error?: string };
				setErrorMsg(data.error === "too_many_requests" ? t("formErrorRateLimit") : t("formError"));
				setStatus("error");
				return;
			}
			setStatus("success");
		} catch {
			setErrorMsg(t("formError"));
			setStatus("error");
		}
	}

	if (status === "success") {
		return (
			<div className="border-success/30 bg-success/[0.08] rounded-2xl border p-6 text-center">
				<div className="bg-success/20 mx-auto flex h-12 w-12 items-center justify-center rounded-full">
					<svg
						viewBox="0 0 24 24"
						className="h-6 w-6 text-success"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden
					>
						<path d="M2 12 L9 19 L22 5" />
					</svg>
				</div>
				<div className="mt-4 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-success">
					{t("formSuccess")}
				</div>
				<h3 className="mt-2 font-display text-[1.15rem] font-medium tracking-[-0.01em] text-foreground">
					{kitT("contact.title")}
				</h3>
				<p className="mt-2 text-[0.85rem] leading-relaxed text-muted-foreground">{kitT("contact.success")}</p>
			</div>
		);
	}

	const labelClass =
		"block font-mono text-[0.58rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground";
	const inputClass =
		"h-12 w-full rounded-lg border border-border bg-background px-3.5 text-[0.95rem] text-foreground placeholder:text-muted-foreground/60 focus:border-primary-hero focus:outline-none focus:ring-2 focus:ring-primary-hero/15";

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{/* Name */}
			<div>
				<label htmlFor="hcf-name" className={labelClass}>
					{t("formName")}
				</label>
				<input
					id="hcf-name"
					type="text"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder={t("formName")}
					className={`${inputClass} mt-1.5`}
					required
				/>
			</div>

			{/* Email */}
			<div>
				<label htmlFor="hcf-email" className={labelClass}>
					{t("formEmail")}
				</label>
				<input
					id="hcf-email"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder="name@example.com"
					className={`${inputClass} mt-1.5`}
					required
				/>
			</div>

			{/* Topic */}
			<div>
				<label htmlFor="hcf-topic" className={labelClass}>
					{t("formTopic")}
				</label>
				<div className="relative mt-1.5">
					<select
						id="hcf-topic"
						value={topic}
						onChange={(e) => setTopic(e.target.value as HomeContactTopic)}
						className={`${inputClass} appearance-none pr-10`}
						required
					>
						<option value="" disabled>
							{t("formTopicPlaceholder")}
						</option>
						{topics.map((topic) => (
							<option key={topic.id} value={topic.id}>
								{topic.label}
							</option>
						))}
					</select>
					<svg
						aria-hidden
						viewBox="0 0 12 8"
						className="pointer-events-none absolute right-4 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-muted-foreground"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M1 1 L6 7 L11 1" />
					</svg>
				</div>
			</div>

			{/* Message */}
			<div>
				<label htmlFor="hcf-message" className={labelClass}>
					{t("formMessage")}
				</label>
				<textarea
					id="hcf-message"
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					placeholder={kitT("contact.messagePlaceholder")}
					rows={4}
					className={`placeholder:text-muted-foreground/60 focus:ring-primary-hero/15 mt-1.5 w-full resize-y rounded-lg border border-border bg-background px-3.5 py-3 text-[0.95rem] leading-relaxed text-foreground focus:border-primary-hero focus:outline-none focus:ring-2`}
					required
				/>
			</div>

			{/* Consent */}
			<label className="flex items-start gap-2.5 pt-1">
				<input
					type="checkbox"
					checked={consent}
					onChange={(e) => setConsent(e.target.checked)}
					className="border-primary-hero/40 mt-0.5 h-4 w-4 shrink-0 rounded border-2 text-primary-hero focus:ring-primary-hero"
					required
				/>
				<span className="text-[0.72rem] leading-snug text-muted-foreground">
					{t("formConsentPrefix")}
					<a
						href={publicStorePath(locale, "/privacy")}
						className="text-foreground underline-offset-2 hover:underline"
						target="_blank"
						rel="noreferrer"
					>
						{t("formConsentPrivacy")}
					</a>
					{t("formConsentSuffix")}
				</span>
			</label>

			{/* Error message */}
			{errorMsg && (
				<div className="border-destructive/20 bg-destructive/10 rounded-lg border px-3 py-2 font-mono text-[0.7rem] text-destructive">
					{errorMsg}
				</div>
			)}

			{/* Submit */}
			<button
				type="submit"
				disabled={!isValid || status === "loading"}
				className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary-hero font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary disabled:opacity-50"
			>
				{status === "loading" ? t("formSubmitting") : t("formSubmit")}
			</button>
		</form>
	);
}
